import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import { Vault, getVault } from 'src/pages/admin/services/vaultService';
import { parseApiError } from 'src/pages/admin/utils/decimal-input';

import { listErrorMessage } from './helpers';

/** The BFF's answer for an id the catalog does not know (C-06 VaultAdmin_VaultNotFound). */
const NOT_FOUND = 'VAULT_NOT_FOUND';

/**
 * `GET /vault/vaults/:id` (XBIT-4239, §14.4 detail). `notFound` is an id the catalog does not know — the BFF's
 * `VAULT_NOT_FOUND`, never a bare 404 — which the page answers by going back to the list. A route the BFF does not
 * have, or a proxy's 404, is a failure like any other: a banner, so web/BFF version skew is seen, not hidden (ea's
 * review m2; Investors keys on the code alone too). A failure keeps the vault last shown for this id.
 */
export const useVaultDetail = (vaultId: string | undefined) => {
  const translate = useTranslate();
  // react-admin's translate is not identity-stable; a ref keeps refetch stable (see useVaults).
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const [vault, setVault] = useState<Vault | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const refetch = useCallback(async () => {
    if (!vaultId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setVault(await getVault(vaultId));
      setLoadError(null);
      setNotFound(false);
    } catch (error) {
      const info = parseApiError(error);
      if (info.code === NOT_FOUND) setNotFound(true);
      else setLoadError(listErrorMessage(info, translateRef.current));
    } finally {
      setLoading(false);
    }
  }, [vaultId]);

  useEffect(() => {
    // Another vault's figures must never sit under this id's banner.
    setVault(null);
    refetch();
  }, [refetch]);

  return { vault, loading, loadError, notFound, refetch };
};
